import type { WfhRequestFilters } from '../types/wfh';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Default WFH history window is the current calendar year.
 * Backend requires fromDate and toDate; history has no status/type/search filters.
 */
export function createDefaultWfhDateRange(now = new Date()): {
  fromDate: string;
  toDate: string;
} {
  const year = now.getFullYear();
  return {
    fromDate: `${year}-01-01`,
    toDate: `${year}-12-31`,
  };
}

export function createDefaultWfhFilters(now = new Date()): WfhRequestFilters {
  return createDefaultWfhDateRange(now);
}

export function isValidWfhDate(value: string): boolean {
  if (!DATE_ONLY.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

export function validateWfhDateRange(
  fromDate: string,
  toDate: string,
): string | null {
  if (!isValidWfhDate(fromDate)) {
    return 'From date must use YYYY-MM-DD format.';
  }
  if (!isValidWfhDate(toDate)) {
    return 'To date must use YYYY-MM-DD format.';
  }
  if (fromDate > toDate) {
    return 'The selected date range is invalid.';
  }
  return null;
}

export function normalizeWfhFilters(input: {
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}): {
  filters: WfhRequestFilters;
  page: number;
  pageSize: number;
} {
  const defaults = createDefaultWfhDateRange();
  const fromDate = input.fromDate?.trim() || defaults.fromDate;
  const toDate = input.toDate?.trim() || defaults.toDate;
  const rangeError = validateWfhDateRange(fromDate, toDate);
  if (rangeError) {
    throw Object.assign(new Error(rangeError), { code: 'INVALID_DATE_RANGE' });
  }

  const page = input.page ?? 1;
  const pageSize = input.pageSize ?? 20;
  if (!Number.isInteger(page) || page <= 0) {
    throw Object.assign(new Error('page must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!Number.isInteger(pageSize) || pageSize <= 0 || pageSize > 100) {
    throw Object.assign(new Error('pageSize must be between 1 and 100'), {
      code: 'VALIDATION_ERROR',
    });
  }

  return {
    filters: { fromDate, toDate },
    page,
    pageSize,
  };
}
